/**
 * AYRAA — running-content marquee.
 *
 * The theme animates this band from JavaScript: ECXMarqueeSection runs a
 * requestAnimationFrame loop that moves the track by `normal-speed / 2`
 * pixels on every frame and writes style.transform each time.
 *
 * Two problems with that.
 *
 *   1. Pixels per FRAME is not a speed. At the stored setting of 6 the track
 *      moves 3px a frame, which is 180px/s on a 60Hz screen and 360px/s on a
 *      120Hz one. The same store looks calm on one laptop and frantic on the
 *      next, and there is no setting that fixes both.
 *
 *   2. It never stops. The loop re-schedules itself forever, scrolled out of
 *      view or not, writing a transform from the main thread every frame.
 *      That is the pair of non-composited animations Lighthouse flags.
 *
 * So the loop is switched off and the same movement is expressed as a CSS
 * animation, which the compositor runs off the main thread and which is
 * timed in seconds rather than frames. All this file does is the one thing
 * CSS cannot: measure how wide a single copy of the strip is and convert the
 * merchant's speed setting into a duration. That happens once on load and
 * again on resize — not every frame.
 *
 * The markup, the copies and the section settings are untouched.
 */

(function () {
  'use strict';

  /* The tag is emitted by the section, so a page with two running-content
     sections asks for it twice. The browser fetches it once, but the module
     body would still run twice and stack duplicate listeners. */
  if (window.__ayMarqueeReady) return;
  window.__ayMarqueeReady = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The setting is a 1-10 slider. Ten pixels a second per step puts the
     stored 6 at 60px/s — about a third of what the frame loop was doing on a
     60Hz screen, and the same on every screen. The slider still means what a
     merchant expects: higher is faster. */
  var PX_PER_SECOND_PER_STEP = 10;
  var MIN_DURATION = 4;

  /* ----------------------------------------------------------------------
     Stop the theme's frame loop.

     The class is reachable through the custom element registry, so the
     prototype can be replaced without touching theme.js — which is minified
     vendor code that a theme update would overwrite. animate() re-binds
     itself from the prototype on each frame, so a pending frame runs at most
     once more and then finds this no-op instead. checkWidth() goes too: it
     only exists to feed the loop, and it sets a min-width on the copies that
     would fight the spacing rules in ay-base.css.
     ---------------------------------------------------------------------- */
  function releaseThemeLoop() {
    var Ctor = window.customElements && window.customElements.get('ecx-marquee-section');
    if (!Ctor || Ctor.prototype.__ayReleased) return;
    Ctor.prototype.animate = function () {};
    Ctor.prototype.checkWidth = function () {};
    Ctor.prototype.__ayReleased = true;
  }

  /* ----------------------------------------------------------------------
     Measure one copy and hand CSS a duration.
     ---------------------------------------------------------------------- */
  function measure(section) {
    var track = section.querySelector('.js-marquee-container');
    var copy = section.querySelector('.js-marquee-list');
    if (!track || !copy) return;

    var copies = track.children.length;
    if (!copies) return;

    /* The theme's loop may have written an inline transform before it was
       switched off. A CSS animation outranks an inline style so it would not
       actually show, but leaving it there is misleading to read in devtools. */
    track.style.transform = '';
    Array.prototype.forEach.call(track.querySelectorAll('.js-marquee-list'), function (el) {
      el.style.minWidth = '';
    });

    var width = copy.getBoundingClientRect().width;
    if (!width) return;

    var step = parseFloat(section.getAttribute('normal-speed'));
    if (!isFinite(step) || step <= 0) step = 6;

    var duration = width / (step * PX_PER_SECOND_PER_STEP);
    if (duration < MIN_DURATION) duration = MIN_DURATION;

    track.style.setProperty('--ay-marquee-copies', copies);
    track.style.setProperty('--ay-marquee-duration', duration.toFixed(2) + 's');

    /* direction="animation-reverse" is the theme's way of saying scroll right. */
    if (section.getAttribute('direction') === 'animation-reverse') {
      track.setAttribute('data-ay-marquee-reverse', '');
    }
    section.setAttribute('data-ay-marquee', 'on');
  }

  function measureAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('ecx-marquee-section'),
      measure
    );
  }

  /* ----------------------------------------------------------------------
     Boot.

     This file is pulled in by the section, so it can run either side of
     theme.js depending on where the section sits in the page. Ordering is
     therefore made explicit rather than assumed: whenDefined() fires as soon
     as the theme registers the element, whichever ran first, and by then the
     instances have upgraded and their loops have started — so that is the
     moment to switch them off. Measuring is independent of all that, because
     the CSS animation only needs the two custom properties; if theme.js never
     loads at all there is no loop to stop and the band still scrolls.
     ---------------------------------------------------------------------- */
  function boot() {
    releaseThemeLoop();
    if (REDUCED.matches) return; // CSS holds it still; no point measuring
    measureAll(document);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(boot);

  if (window.customElements) {
    window.customElements.whenDefined('ecx-marquee-section').then(function () {
      onReady(boot);
    });
  }

  /* A copy's width changes with the viewport, so the duration has to follow
     or the speed drifts between breakpoints. Debounced — this reads layout. */
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (!REDUCED.matches) measureAll(document);
    }, 200);
  });

  REDUCED.addEventListener('change', function () {
    if (!REDUCED.matches) measureAll(document);
  });

  /* Theme editor: a re-rendered section arrives with a fresh element that the
     theme upgrades and starts looping again. */
  document.addEventListener('shopify:section:load', function (event) {
    releaseThemeLoop();
    if (!REDUCED.matches) measureAll(event.target);
  });
})();
